import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from '../i18n/LanguageContext';

const ContactSection = () => {
  const { toast } = useToast();
  const { t, language } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'business',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: t('contact.form.success'),
          description: data.message || t('contact.form.successMessage')
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'business',
          message: ''
        });
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: 'Fehler',
        description: 'Ihre Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="section-spacing-large bg-white">
      <div className="container-cift">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="gold-accent mx-auto" />
          <h2 className="section-title mb-6">
            {t('contact.headline')}
          </h2>
          <p className="subtitle">
            {t('contact.body')}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="text-2xl font-display font-bold mb-6">
                {t('contact.info.headline')}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-gold" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">{t('contact.info.email')}</p>
                    <a
                      href="mailto:Cift.Studio@gmail.com"
                      className="text-gray-600 hover:text-gold transition-colors">

                      Cift.Studio@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-gold" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">{t('contact.info.instagram')}</p>
                    <a
                      href="https://instagram.com/cift.wedding"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-gold transition-colors">@cift.studio


                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-gold" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">{t('contact.info.website')}</p>
                    <a
                      href="https://ciftstudio.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-gold transition-colors">

                      ciftstudio.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-cream p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">
                {t('contact.info.quickResponse')}
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t('contact.info.quickResponseText')}
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('contact.form.name')} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gold focus:outline-none transition-colors"
                  placeholder="Max Mustermann" />

              </div>

              {/* Email & Phone */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    {t('contact.form.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gold focus:outline-none transition-colors"
                    placeholder="max@beispiel.de" />

                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    {t('contact.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gold focus:outline-none transition-colors"
                    placeholder="+49 123 456789" />

                </div>
              </div>

              {/* Project Type */}
              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('contact.form.projectType')} *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  required
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gold focus:outline-none transition-colors bg-white">

                  <option value="business">{t('contact.form.projectTypes.business')}</option>
                  <option value="wedding">{t('contact.form.projectTypes.wedding')}</option>
                  <option value="other">{t('contact.form.projectTypes.other')}</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  {t('contact.form.message')} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gold focus:outline-none transition-colors resize-none"
                  placeholder={t('contact.form.messagePlaceholder')} />

              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed">

                {isSubmitting ? t('contact.form.sending') || 'Senden...' : t('contact.form.submit')}
                <Send size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Datenschutz-Hinweis */}
              <p className="text-gray-600 italic text-center" style={{ fontSize: '13px' }}>
                {language === 'de' ? (
                  <>
                    Mit dem Absenden dieses Formulars stimmen Sie der Verarbeitung Ihrer Daten gemäß unserer{' '}
                    <Link to="/datenschutz" className="text-brand-gold hover:underline">
                      Datenschutzerklärung
                    </Link>
                    {' '}zu.
                  </>
                ) : (
                  <>
                    By submitting this form, you agree to the processing of your data in accordance with our{' '}
                    <Link to="/datenschutz" className="text-brand-gold hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </>
                )}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

export default ContactSection;